import { useMemo } from "react";
import { useQuery, useQueries } from "@tanstack/react-query";
import { CUSTOMERS_QUERY_KEY } from "./useCustomerOverview";
import {
  fetchActiveLicenseOverview,
  fetchCustomerLicenseTrend,
  fetchExpiringSoonLicenseOverview,
  type ActiveLicenseOverviewItem,
  type ActiveLicenseOverviewResponse,
  type ExpiringSoonLicenseOverviewItem,
  type ExpiringSoonLicenseOverviewResponse,
  type LicenseTrendMode,
  type LicenseTrendPoint,
  type LicenseTrendResponse,
} from "../api/licenseOverviewApi";

interface UseExpiringSoonLicenseOverviewParams {
  company?: string;
  page: number;
  pageSize: number;
}

interface UseCustomerLicenseTrendParams {
  mode: LicenseTrendMode;
  dateFrom?: string;
  dateTo?: string;
}

const ACTIVE_DEFAULT_SORT = "brand_grouping,product_model,pip";
const EXPIRING_DEFAULT_SORT = "warranty_end_date,brand_grouping,product_model,pip";

const DEFAULT_MULTI_EXPIRING_PAGE_SIZE = 500;

function normalizeKey(value?: string | null) {
  return (value ?? "").trim().toLowerCase();
}

function normalizeProductModel(value?: string | null) {
  return (value ?? "").trim().toLowerCase();
}

function mergeActiveItems(
  datasets: ActiveLicenseOverviewItem[][]
): ActiveLicenseOverviewItem[] {
  const grouped = new Map<string, ActiveLicenseOverviewItem>();

  for (const items of datasets) {
    for (const item of items) {
      const key = [
        normalizeKey(item.brand_grouping),
        normalizeProductModel(item.product_model),
      ].join("::");
      const existing = grouped.get(key);
      if (existing) {
        existing.sum_qty = (existing.sum_qty ?? 0) + (item.sum_qty ?? 0);
        existing.sum_pip_total =
          (existing.sum_pip_total ?? 0) + (item.sum_pip_total ?? 0);
        existing.pip = existing.pip ?? item.pip;
        continue;
      }
      grouped.set(key, {
        ...item,
      });
    }
  }

  return Array.from(grouped.values()).sort((left, right) => {
    const brandCompare = left.brand_grouping.localeCompare(right.brand_grouping);
    if (brandCompare !== 0) return brandCompare;
    const leftModel = normalizeProductModel(left.product_model);
    const rightModel = normalizeProductModel(right.product_model);
    return leftModel.localeCompare(rightModel);
  });
}

function mergeExpiringItems(
  datasets: ExpiringSoonLicenseOverviewItem[][]
): ExpiringSoonLicenseOverviewItem[] {
  const grouped = new Map<string, ExpiringSoonLicenseOverviewItem>();

  for (const items of datasets) {
    for (const item of items) {
      const key = [
        item.warranty_end_date,
        normalizeKey(item.brand_grouping),
        normalizeProductModel(item.product_model),
        item.pip == null ? "null" : String(item.pip),
      ].join("::");
      const existing = grouped.get(key);
      if (existing) {
        existing.sum_qty = (existing.sum_qty ?? 0) + (item.sum_qty ?? 0);
        existing.sum_pip_total =
          (existing.sum_pip_total ?? 0) + (item.sum_pip_total ?? 0);
        continue;
      }
      grouped.set(key, {
        ...item,
      });
    }
  }

  return Array.from(grouped.values()).sort((left, right) => {
    const dateCompare = left.warranty_end_date.localeCompare(
      right.warranty_end_date
    );
    if (dateCompare !== 0) return dateCompare;
    const brandCompare = left.brand_grouping.localeCompare(right.brand_grouping);
    if (brandCompare !== 0) return brandCompare;
    const modelCompare = normalizeProductModel(left.product_model).localeCompare(
      normalizeProductModel(right.product_model)
    );
    if (modelCompare !== 0) return modelCompare;
    return (left.pip ?? 0) - (right.pip ?? 0);
  });
}

async function fetchAllExpiringSoonForCompany(company: string) {
  let page = 1;
  const items: ExpiringSoonLicenseOverviewItem[] = [];
  let total = 0;

  while (true) {
    const response = await fetchExpiringSoonLicenseOverview({
      company,
      page,
      page_size: DEFAULT_MULTI_EXPIRING_PAGE_SIZE,
      sort: EXPIRING_DEFAULT_SORT,
    });
    items.push(...(response.items ?? []));
    total = response.pagination?.total ?? items.length;
    if (items.length >= total || (response.items ?? []).length === 0) {
      break;
    }
    page += 1;
  }

  return items;
}

function mergeTrendSeries(datasets: LicenseTrendPoint[][]): LicenseTrendPoint[] {
  const grouped = new Map<string, LicenseTrendPoint>();

  for (const series of datasets) {
    for (const point of series) {
      const existing = grouped.get(point.period_start);
      if (existing) {
        existing.autodesk += point.autodesk ?? 0;
        existing.bluebeam += point.bluebeam ?? 0;
        existing.sketchup += point.sketchup ?? 0;
        existing.chaosgroup += point.chaosgroup ?? 0;
        existing.adobe += point.adobe ?? 0;
        existing.others += point.others ?? 0;
        existing.total_active_licenses += point.total_active_licenses ?? 0;
        continue;
      }
      grouped.set(point.period_start, { ...point });
    }
  }

  return Array.from(grouped.values()).sort((left, right) =>
    left.period_start.localeCompare(right.period_start)
  );
}

export function useActiveLicenseOverview(params: { company?: string }) {
  const trimmedCompany = params.company?.trim();
  const enabled = !!trimmedCompany && !trimmedCompany.startsWith("Company #");

  return useQuery<ActiveLicenseOverviewResponse>({
    queryKey: [
      CUSTOMERS_QUERY_KEY,
      "license-overview",
      "active-all",
      trimmedCompany ?? null,
    ],
    queryFn: () =>
      fetchActiveLicenseOverview({
        company: trimmedCompany,
        sort: ACTIVE_DEFAULT_SORT,
      }),
    enabled,
  });
}

export function useExpiringSoonLicenseOverview(
  params: UseExpiringSoonLicenseOverviewParams
) {
  const trimmedCompany = params.company?.trim();
  const enabled = !!trimmedCompany && !trimmedCompany.startsWith("Company #");

  return useQuery<ExpiringSoonLicenseOverviewResponse>({
    queryKey: [
      CUSTOMERS_QUERY_KEY,
      "license-overview",
      "expiring-soon",
      trimmedCompany ?? null,
      params.page,
      params.pageSize,
    ],
    queryFn: () =>
      fetchExpiringSoonLicenseOverview({
        company: trimmedCompany,
        page: params.page,
        page_size: params.pageSize,
        sort: EXPIRING_DEFAULT_SORT,
      }),
    enabled,
  });
}

export function useCustomerLicenseTrend(
  companyPartnerId?: number,
  params?: UseCustomerLicenseTrendParams
) {
  return useQuery<LicenseTrendResponse>({
    queryKey: [
      CUSTOMERS_QUERY_KEY,
      "license-overview",
      "trend",
      companyPartnerId ?? null,
      params?.mode ?? null,
      params?.dateFrom ?? null,
      params?.dateTo ?? null,
    ],
    queryFn: () => {
      if (!companyPartnerId) {
        throw new Error("Missing company partner id");
      }

      return fetchCustomerLicenseTrend(companyPartnerId, {
        mode: params?.mode ?? "monthly",
        date_from: params?.dateFrom,
        date_to: params?.dateTo,
      });
    },
    enabled: !!companyPartnerId,
    refetchOnWindowFocus: false,
  });
}

export function useMultiCompanyLicenseTrend(
  companyPartnerIds: number[] | undefined,
  params?: UseCustomerLicenseTrendParams
) {
  const normalizedIds = useMemo(
    () => Array.from(new Set((companyPartnerIds ?? []).filter(Boolean))),
    [companyPartnerIds]
  );

  const queries = useQueries({
    queries: normalizedIds.map((companyPartnerId) => ({
      queryKey: [
        CUSTOMERS_QUERY_KEY,
        "license-overview",
        "trend-multi",
        companyPartnerId,
        params?.mode ?? null,
        params?.dateFrom ?? null,
        params?.dateTo ?? null,
      ],
      queryFn: () =>
        fetchCustomerLicenseTrend(companyPartnerId, {
          mode: params?.mode ?? "monthly",
          date_from: params?.dateFrom,
          date_to: params?.dateTo,
        }),
      enabled: normalizedIds.length > 0,
      refetchOnWindowFocus: false,
    })),
  });

  const data = useMemo<LicenseTrendResponse | undefined>(() => {
    if (!normalizedIds.length) return undefined;
    if (queries.some((query) => query.isLoading || query.isFetching)) {
      return undefined;
    }
    if (queries.some((query) => query.error)) {
      return undefined;
    }

    const merged = mergeTrendSeries(
      queries
        .map((query) => query.data?.series ?? [])
        .filter((series) => series.length > 0)
    );

    return {
      company_partner_id: normalizedIds[0],
      company_name: "Multi companies",
      mode: params?.mode ?? "monthly",
      series: merged,
    };
  }, [normalizedIds, params?.mode, queries]);

  const isLoading =
    normalizedIds.length > 0 &&
    queries.some((query) => query.isLoading || query.isFetching);

  const error = queries.find((query) => query.error)?.error ?? null;

  return {
    data,
    isLoading,
    error,
  };
}

export function useMultiCompanyLicenseOverview(companyNames: string[] | undefined) {
  const normalizedCompanyNames = useMemo(
    () =>
      Array.from(
        new Set((companyNames ?? []).map((name) => name.trim()).filter(Boolean))
      ),
    [companyNames]
  );

  const activeQueries = useQueries({
    queries: normalizedCompanyNames.map((company) => ({
      queryKey: [
        CUSTOMERS_QUERY_KEY,
        "license-overview",
        "active-multi",
        company,
      ],
      queryFn: () =>
        fetchActiveLicenseOverview({
          company,
          sort: ACTIVE_DEFAULT_SORT,
        }),
      enabled: normalizedCompanyNames.length > 0,
      refetchOnWindowFocus: false,
    })),
  });

  const expiringQueries = useQueries({
    queries: normalizedCompanyNames.map((company) => ({
      queryKey: [
        CUSTOMERS_QUERY_KEY,
        "license-overview",
        "expiring-multi",
        company,
      ],
      queryFn: async () => ({
        items: await fetchAllExpiringSoonForCompany(company),
      }),
      enabled: normalizedCompanyNames.length > 0,
      refetchOnWindowFocus: false,
    })),
  });

  const activeItems = useMemo(
    () =>
      mergeActiveItems(
        activeQueries
          .map((query) => query.data?.items ?? [])
          .filter((items) => items.length > 0)
      ),
    [activeQueries]
  );

  const expiringItems = useMemo(
    () =>
      mergeExpiringItems(
        expiringQueries
          .map((query) => query.data?.items ?? [])
          .filter((items) => items.length > 0)
      ),
    [expiringQueries]
  );

  const isLoading =
    normalizedCompanyNames.length > 0 &&
    (activeQueries.some((query) => query.isLoading || query.isFetching) ||
      expiringQueries.some((query) => query.isLoading || query.isFetching));

  const error =
    activeQueries.find((query) => query.error)?.error ??
    expiringQueries.find((query) => query.error)?.error ??
    null;

  return {
    activeItems,
    expiringItems,
    isLoading,
    error,
  };
}
